import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { 
  Carousel, 
  CarouselContent, 
  CarouselItem, 
  CarouselNext, 
  CarouselPrevious 
} from "@/components/ui/carousel";
import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const products = [
  {
    name: "Helical Gear",
    description: "Precision-manufactured helical gears for textile machinery drive systems. Features angled teeth design for smooth power transmission and reduced noise in high-speed spinning operations.",
    variants: "Various tooth counts and ratios",
    imageUrl: "/images/helical-gear.png"
  },
  {
    name: "Spur Gear",
    description: "High-quality spur gears with precision tooth geometry for reliable power transmission in textile machinery. Features durable construction and smooth operation for extended service life.",
    variants: "Multiple sizes and configurations",
    imageUrl: "/images/spur-gear.jpg"
  },
  {
    name: "Yarn Cutter",
    description: "Precision yarn cutting components designed for clean, accurate cuts in textile manufacturing. Features sharp cutting edges and durable construction for consistent performance.",
    variants: "Standard and custom designs",
    imageUrl: "/images/yarn-cutter.png"
  },
  {
    name: "Separator",
    description: "High-quality separator components for textile machinery applications. Engineered for reliable yarn separation and guidance with smooth surface finish and precise dimensions.",
    variants: "Various specifications available",
    imageUrl: "/images/separator.png"
  },
  {
    name: "Spindle Brake",
    description: "Robust spindle brake assemblies for textile spinning machinery. Features reliable braking mechanism with adjustable pressure settings for optimal spindle control.",
    variants: "Multiple mounting configurations",
    imageUrl: "/images/spindle-brake.png"
  },
  {
    name: "Ring",
    description: "Precision-engineered rings for textile spinning applications. Features smooth surface finish and precise tolerances for optimal yarn quality and consistent performance.",
    variants: "All standard sizes available",
    imageUrl: "/images/ring.png"
  },
  {
    name: "Bobbin Transport System",
    description: "Advanced bobbin transport system designed for optimal performance in textile spinning machinery. Features durable construction and precise engineering for reliable automated operation.",
    variants: "Multiple configurations available",
    imageUrl: "/images/bobbin-transport-system.png"
  }
];

const featuredImages = [
  { url: "/images/IMG-20250207-WA0013.jpg", alt: "Textile machinery component closeup" },
  { url: "/images/IMG-20250207-WA0022.jpg", alt: "Ring traveller assembly" },
  { url: "/images/IMG-20250207-WA0023.jpg", alt: "Spindle component" },
  { url: "/images/IMG-20250207-WA0024.jpg", alt: "Ceramic machinery part" },
  { url: "/images/IMG-20250207-WA0025.jpg", alt: "Industrial textile equipment" }
];

export default function Products() {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const handlePrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? featuredImages.length - 1 : prevIndex - 1
    );
  };
  
  const handleNext = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === featuredImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  return (
    <div className="container py-16 px-4 md:px-6">
      <div className="max-w-3xl">
        <motion.h1 
          className="text-4xl font-bold tracking-tight"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Textile Machinery Components
        </motion.h1>
        <motion.p 
          className="mt-4 text-lg text-muted-foreground leading-relaxed"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          Discover our comprehensive range of high-precision textile machinery parts and components, 
          engineered to meet the demanding requirements of modern textile manufacturing.
        </motion.p>
      </div>

      {/* Our Product Categories Header */}
      <motion.div
        className="mt-16"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        <h2 className="text-3xl font-bold tracking-tight text-center mb-4">Our Product Categories</h2>
        <p className="text-lg text-muted-foreground text-center max-w-2xl mx-auto leading-relaxed">
          Explore our extensive range of precision-engineered textile machinery components designed for optimal performance and reliability.
        </p>
      </motion.div>

      {/* Product Categories Carousel */}
      <motion.div
        className="mt-12"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.6 }}
      >
        <Carousel
          opts={{
            align: "start",
            loop: true,
          }}
          className="w-full"
        >
          <CarouselContent className="-ml-4">
            {products.map((product, index) => (
              <CarouselItem key={`carousel-${product.name}-${index}`} className="pl-4 md:basis-1/2 lg:basis-1/3">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.5,
                    delay: 0.1 * index + 0.7,
                    ease: "easeOut"
                  }}
                  whileHover={{ 
                    scale: 1.30,
                    y: -20,
                    transition: { 
                      duration: 0.25,
                      ease: "easeOut"
                    }
                  }}
                  whileTap={{ scale: 0.98 }}
                  style={{ 
                    transformOrigin: "center center"
                  }}
                  className="cursor-pointer"
                >
                  <Card className="overflow-hidden h-full shadow-lg hover:shadow-2xl transition-all duration-500 border-0 bg-white">
                    <motion.div 
                      className="aspect-video w-full overflow-hidden bg-gradient-to-br from-slate-50 to-white relative"
                      whileHover={{ 
                        scale: 1.45,
                        transition: { 
                          duration: 0.2,
                          ease: "easeOut"
                        }
                      }}
                      animate={{ 
                        scale: 1,
                        transition: { 
                          duration: 0.15,
                          ease: "easeInOut"
                        }
                      }}
                    >
                      <motion.img
                        src={product.imageUrl}
                        alt={product.name}
                        className="h-full w-full object-contain p-4 mix-blend-darken filter brightness-110 contrast-125 saturate-110"
                        loading="lazy"
                        whileHover={{
                          filter: "brightness(1.35) contrast(1.45) saturate(1.35)",
                          transition: { duration: 0.15 }
                        }}
                        animate={{
                          filter: "brightness(1.1) contrast(1.25) saturate(1.1)",
                          transition: { duration: 0.1 }
                        }}
                      />
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0"
                        whileHover={{
                          opacity: 1,
                          transition: { duration: 0.3 }
                        }}
                      />
                    </motion.div>
                    <CardHeader className="p-6">
                      <CardTitle className="text-xl">{product.name}</CardTitle>
                    </CardHeader>
                    <CardContent className="p-6 pt-0">
                      <p className="text-muted-foreground text-sm mb-4 leading-relaxed">{product.description}</p>
                      <p className="text-sm font-medium text-primary">{product.variants}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="-left-4 bg-background/90 hover:bg-background shadow-lg" />
          <CarouselNext className="-right-4 bg-background/90 hover:bg-background shadow-lg" />
        </Carousel>
      </motion.div>
    </div>
  );
}